import { useState, useEffect, useCallback, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { createSelector } from "reselect";
import { toast } from "react-toastify";
import {
  loadAllDeductionCategories,
  loadAllDeductions,
  loadDeductionSettings,
  loadEmployeeAssignedDeduction,
  assignDeductionToEmployee,
  deleteDeductionFromEmployee,
  disableDeductionFromEmployee
} from "../../../../../slice/thunks";
import { useParams, useLocation } from "react-router-dom";
import { parseDate } from "../../../../../components/Common/Utilities";

const useDeductionData = () => {
  const { id: employeeId } = useParams();
  const location = useLocation();
  const dispatch = useDispatch();
  const employeeFromState = location.state?.employee;

  const selectSettingsData = useMemo(
    () =>
      createSelector(
        (state) => state.Payroll,
        (settings) => ({
          deductionCategories: settings.deductionCategories,
          deductionList: settings.deductionList,
          deductionSettings: settings.deductionSettings,
          assignedDeductionsFromStore: settings.assignedDeductions,
          error: settings.error,
        })
      ),
    []
  );

  const {
    deductionCategories,
    deductionList,
    deductionSettings,
    assignedDeductionsFromStore,
    error,
  } = useSelector(selectSettingsData);

  const [employee, setEmployee] = useState(employeeFromState || null);
  const [assignedDeductions, setAssignedDeductions] = useState([]);
  const [selectedDeduction, setSelectedDeductionState] = useState("");
  const [customAmount, setCustomAmount] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [isLoading, setIsLoading] = useState(!employeeFromState);
  const [isLoadingCategories, setIsLoadingCategories] = useState(true);
  const [isLoadingDeductions, setIsLoadingDeductions] = useState(true);
  const [isLoadingSettings, setIsLoadingSettings] = useState(false);
  const [isLoadingAssigned, setIsLoadingAssigned] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDisabling, setIsDisabling] = useState(false);
  const [backendError, setBackendError] = useState("");

  // Modal Delete
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deductionToDelete, setDeductionToDelete] = useState(null);

  // Modal Disable
  const [showDisableModal, setShowDisableModal] = useState(false);
  const [deductionToDisable, setDeductionToDisable] = useState(null);

  const [currentDeduction, setCurrentDeduction] = useState(null);

  // ===== UTILITAIRES =====
  const isDeductionAlreadyAssigned = useCallback(
    (deductionId) => assignedDeductions.some((ad) => ad.deductionId === deductionId),
    [assignedDeductions]
  );

  const handleDeductionSelection = useCallback(
    (deductionId) => {
      if (deductionId && isDeductionAlreadyAssigned(deductionId)) {
        toast.error("deduction already assigned");
        return;
      }
      setSelectedDeductionState(deductionId);
    },
    [isDeductionAlreadyAssigned]
  );

  const openDeleteModal = useCallback((deduction) => {
    setDeductionToDelete(deduction);
    setShowDeleteModal(true);
  }, []);

  const closeDeleteModal = useCallback(() => {
    if (!isDeleting) {
      setShowDeleteModal(false);
      setDeductionToDelete(null);
      setBackendError("");
    }
  }, [isDeleting]);

  const openDisableModal = useCallback((deduction) => {
    setDeductionToDisable(deduction);
    setShowDisableModal(true);
  }, []);

  const closeDisableModal = useCallback(() => {
    if (!isDisabling) {
      setShowDisableModal(false);
      setDeductionToDisable(null);
    }
  }, [isDisabling]);

  const clearForm = useCallback(() => {
    setSelectedDeductionState("");
    setCustomAmount("");
    setStartDate("");
    setEndDate("");
    setCurrentDeduction(null);
    setBackendError("");
  }, []);

  // ===== FETCH INITIAL =====
  useEffect(() => {
    if (!employeeId) return;

    const fetchData = async () => {
      setIsLoading(true);
      setBackendError("");
      try {
        await Promise.all([
          dispatch(loadAllDeductionCategories()).unwrap(),
          dispatch(loadAllDeductions()).unwrap(),
          dispatch(loadEmployeeAssignedDeduction(employeeId)).unwrap(),
        ]);
      } catch (err) {
        const msg = err?.message || "data loading error";
        setBackendError(msg);
        toast.error(msg);
      } finally {
        setIsLoading(false);
        setIsLoadingCategories(false);
        setIsLoadingDeductions(false);
        setIsLoadingAssigned(false);
      }
    };

    fetchData();
  }, [dispatch, employeeId]);

  // ===== FETCH DEDUCTION SETTINGS POUR SELECTED DEDUCTION =====
  useEffect(() => {
    if (!selectedDeduction) return;

    const fetchSettings = async () => {
      setIsLoadingSettings(true);
      setBackendError("");
      try {
        await dispatch(loadDeductionSettings(selectedDeduction)).unwrap();
      } catch (err) {
        const msg =
          err?.message || "data loading error for deduction settings";
        setBackendError(msg);
        toast.error(msg);
      } finally {
        setIsLoadingSettings(false);
      }
    };

    fetchSettings();
  }, [dispatch, selectedDeduction]);

  // ===== SYNC ASSIGNED DEDUCTIONS =====
  useEffect(() => {
    if (assignedDeductionsFromStore && employee) {
      const formatted = assignedDeductionsFromStore.map((item) => {
        const calculatedAmount = item.percentage
          ? (employee.salary * item.rateOrAmount) / 100
          : item.rateOrAmount;
        return {
          id: item.id,
          deductionId: item.deductionId,
          amount: item.percentage ? null : item.rateOrAmount,
          rate: item.percentage ? item.rateOrAmount : null,
          calculatedAmount,
          deduction: {
            id: item.deductionId,
            name: item.deductionName,
            category: { name: item.categoryName },
            deductionType: item.percentage ? "PERCENTAGE" : "FIXED",
          },
          startDate: item.startDate,
          endDate: item.endDate,
        };
      });
      setAssignedDeductions(formatted);
    }
  }, [assignedDeductionsFromStore, employee]);

  // ===== SYNC CUSTOM AMOUNT =====
  useEffect(() => {
    if (!deductionSettings || deductionSettings.length === 0 || !employee) return;
    const salary = employee.salary || 0;
    const matched = deductionSettings.find(
      (s) =>
        (s.salaryMin === null || salary >= s.salaryMin) &&
        (s.salaryMax === null || salary <= s.salaryMax)
    );
    setCustomAmount(matched ? matched.rateOrAmount : "");
  }, [deductionSettings, employee]);

  // ===== CURRENT DEDUCTION =====
  const availableDeductions = useMemo(() => {
    return deductionList
      ?.map((deduction) => {
        const category = deductionCategories?.find(
          (cat) => cat.name.toLowerCase() === deduction.categoryName?.toLowerCase()
        );
        return {
          ...deduction,
          category:
            category ||
            { id: deduction.categoryId || Date.now().toString(), name: deduction.categoryName || "Non catégorisé" },
          deductionType: deduction.deductionType === "VARIABLE" ? "PERCENTAGE" : "FIXED",
        };
      })
      .filter((deduction) => !isDeductionAlreadyAssigned(deduction.id)) || [];
  }, [deductionList, deductionCategories, isDeductionAlreadyAssigned]);

  useEffect(() => {
    if (!selectedDeduction || !customAmount || !startDate) {
      setCurrentDeduction(null);
      return;
    }
    const deduction = availableDeductions.find((d) => d.id === selectedDeduction);
    if (!deduction) {
      setCurrentDeduction(null);
      return;
    }

    let calculatedAmount = 0, amount = null, rate = null;
    if (deduction.deductionType === "PERCENTAGE") {
      rate = parseFloat(customAmount) || 0;
      calculatedAmount = employee?.salary ? (employee.salary * rate) / 100 : 0;
    } else {
      amount = parseFloat(customAmount) || 0;
      calculatedAmount = amount;
    }

    setCurrentDeduction({
      deductionId: deduction.id,
      amount,
      rate,
      calculatedAmount,
      deduction,
      startDate,
      endDate: endDate || null,
    });
  }, [selectedDeduction, customAmount, startDate, endDate, employee?.salary, availableDeductions]);

  // ===== HANDLERS =====
  const handleSubmit = useCallback(async () => {
    if (!currentDeduction) return;
    if (isDeductionAlreadyAssigned(currentDeduction.deductionId)) {
      toast.error("deduction already assigned");
      return;
    }

    setIsSaving(true);
    setBackendError("");
    try {
      const payload = {
        deductionId: currentDeduction.deductionId,
        percentage: currentDeduction.deduction.deductionType === "PERCENTAGE",
        rateOrAmount:
          currentDeduction.deduction.deductionType === "PERCENTAGE"
            ? currentDeduction.rate.toString()
            : currentDeduction.amount.toString(),
        startDate: currentDeduction.startDate || null,
        endDate: currentDeduction.endDate || null,
      };

      await dispatch(assignDeductionToEmployee({ employeeId, data: payload })).unwrap();
      await dispatch(loadEmployeeAssignedDeduction(employeeId)).unwrap();
      toast.success("Deduction assigned successfully");
      clearForm();
    } catch (err) {
      const msg = err?.message || err?.data?.message || "Error assigning deduction";
      setBackendError(msg);
      toast.error(msg);
    } finally {
      setIsSaving(false);
    }
  }, [currentDeduction, dispatch, employeeId, clearForm, isDeductionAlreadyAssigned]);

  const handleRemoveDeduction = useCallback(
    async (assignedDeduction) => {
      setIsDeleting(true);
      setBackendError("");
      try {
        await dispatch(deleteDeductionFromEmployee({
          employeeId,
          employeeDeductionId: assignedDeduction.id,
        })).unwrap();
        await dispatch(loadEmployeeAssignedDeduction(employeeId)).unwrap();
        toast.success(`Déduction "${assignedDeduction.deduction.name}" delete succesfully`);
        setShowDeleteModal(false);
        setDeductionToDelete(null);
      } catch (err) {
        const msg = err?.message || err?.data?.message || "deleting error";
        setBackendError(msg);
        toast.error(msg);
      } finally {
        setIsDeleting(false);
      }
    },
    [dispatch, employeeId]
  );

  const handleDisableDeduction = useCallback(async () => {
    if (!deductionToDisable) return;
    setIsDisabling(true);
    try {
      await dispatch(disableDeductionFromEmployee({
        employeeId,
        employeeDeductionId: deductionToDisable.id
      })).unwrap();
      await dispatch(loadEmployeeAssignedDeduction(employeeId)).unwrap();
      toast.success(`Déduction "${deductionToDisable.deduction.name}" désactivée !`);
      setShowDisableModal(false);
      setDeductionToDisable(null);
    } catch (err) {
      toast.error(err?.message || "Erreur lors de la désactivation");
    } finally {
      setIsDisabling(false);
    }
  }, [deductionToDisable, dispatch, employeeId]);

  const tableData = useMemo(
    () => assignedDeductions.map((ad) => ({
      ...ad,
      valueDisplay: ad.deduction.deductionType === "PERCENTAGE" ? `${ad.rate}%` : `${ad.amount?.toLocaleString()}`,
      startDateFormatted: parseDate(ad.startDate),
      endDateFormatted: parseDate(ad.endDate),

    })),
    [assignedDeductions]
  );

  const deductionColumns = useMemo(
    () => [
      { header: "Deduction", accessorKey: "deduction.name", enableColumnFilter: false },
      { header: "Category", accessorKey: "deduction.category.name", enableColumnFilter: false,
        cell: ({ getValue }) => {
          const value = getValue();
          return value ? <span className="badge bg-secondary">{value.charAt(0).toUpperCase() + value.slice(1).toLowerCase()}</span> : "";
        }
      },
      { header: "Type", accessorKey: "deduction.deductionType", enableColumnFilter: false,
        cell: ({ getValue }) => {
          const type = getValue();
          return <span className={`badge ${type==="PERCENTAGE"?"bg-info":"bg-warning"}`}>{type==="PERCENTAGE"?"Pourcentage":"Fixe"}</span>;
        }
      },
      { header: "Rate", accessorKey: "valueDisplay", enableColumnFilter: false },
      { header: "Amount", accessorKey: "calculatedAmount", enableColumnFilter: false },
      { header: "Start Date", accessorKey: "startDateFormatted", enableColumnFilter: false },
      { header: "End Date", accessorKey: "endDateFormatted", enableColumnFilter: false },
    ],
    []
  );

  const deductionsByCategory = useMemo(() => {
    return availableDeductions.reduce((acc, d) => {
      const catName = d.category?.name || "Non catégorisé";
      if (!acc[catName]) acc[catName] = [];
      acc[catName].push(d);
      return acc;
    }, {});
  }, [availableDeductions]);

  const totalDeductions = useMemo(
    () => assignedDeductions.reduce((total, ad) => total + (ad.calculatedAmount || 0), 0),
    [assignedDeductions]
  );

  const netSalary = useMemo(
    () => (employee ? employee.salary - totalDeductions : 0),
    [employee, totalDeductions]
  );

  return {
    employee,
    isLoading: isLoading || isLoadingCategories || isLoadingDeductions || isLoadingSettings,
    isSaving,
    isDeleting,
    isDisabling,
    successMessage,
    backendError,
    assignedDeductions,
    allDeductions: availableDeductions,
    deductionsByCategory,
    totalDeductions,
    netSalary,
    selectedDeduction,
    setSelectedDeduction: handleDeductionSelection,
    customAmount,
    setCustomAmount,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    currentDeduction,
    handleSubmit,
    handleRemoveDeduction,
    openDeleteModal,
    closeDeleteModal,
    showDeleteModal,
    deductionToDelete,
    openDisableModal,
    closeDisableModal,
    showDisableModal,
    deductionToDisable,
    handleDisableDeduction,
    clearForm,
    tableData,
    deductionColumns,
    isDeductionAlreadyAssigned,
  };
};

export default useDeductionData;
